import React, { useState } from 'react'
import { Link } from 'react-router'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { PermissionNames } from '@getgreenline/homi-shared'
import { IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { Routes } from '../../../../constants/Routes'
import { InventoryCorrectionsRow } from './InventoryCorrectionsRow'
import { Collapse } from 'reactstrap'
import { ViewAllLogsModalOCS } from './ViewAllLogsModalOCS'
import { ModifyLogModalOCS } from './ModifyLogModalOCS'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { getTotalUnitDiscrepancies } from '../../../../utilities/helpers'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface Props {
  onUpdate: () => void
  logs: IInventoryLog[]
  locationIds: number[]
  startDate: Date
  endDate: Date
}

const _InventoryCorrectionsLogs = ({
  currentCompanyStore,
  currentUserStore,
  onUpdate,
  logs,
  locationIds,
  startDate,
  endDate,
}: Props & InjectedProps) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [showAllLogsModal, setShowAllLogsModal] = useState<boolean>(false)
  const [selectedInventoryLogToModify, setSelectedInventoryLogToModify] =
    useState<IInventoryLog | undefined>(undefined)

  const company = currentCompanyStore?.company
  const { companyId, productId, productName, parentProductId, isProductDeleted, productSku } =
    logs[0]
  const { total, sumOfAdditions, sumOfReductions } = getTotalUnitDiscrepancies(logs)

  const canUpdateInventoryLogs =
    currentUserStore && company
      ? currentUserStore?.hasPermissionAtEntity(
          PermissionNames.CAN_UPDATE_INVENTORY_LOGS,
          company.id,
        )
      : false

  return (
    <>
      {!company ? (
        <p>Loading...</p>
      ) : (
        <>
          <tr className='cursor-pointer' onClick={() => setExpanded(!expanded)}>
            <td> {productSku || '-'}</td>
            <td>
              <Link
                target='_blank'
                to={Routes.EDIT_PRODUCT(company.id, parentProductId || productId)}
              >
                {productName}
              </Link>
              {isProductDeleted && <span className='badge badge-danger ml-2 mr-2'>DELETED</span>}
            </td>
            <td className='text-right'>+{sumOfAdditions}</td>
            <td className='text-right'>{sumOfReductions}</td>
            <td className={'text-right ' + (total < 0 ? 'text-danger' : 'text-success')}>
              <b>{total > 0 ? `+${total}` : total}</b>
            </td>
            <td className='text-right pr-2'>
              <button
                className='btn btn-outline-primary btn-circle shadow-sm'
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <span className='fa fa-angle-up' />
                ) : (
                  <span className='fa fa-angle-down' />
                )}
              </button>
            </td>
          </tr>

          <tr className='bg-light'>
            <td colSpan={6} className='p-0'>
              <Collapse isOpen={expanded}>
                <div className='text-right m-3'>
                  <BlazeButton
                    buttonProps={{
                      type: 'default',
                      onClick: () => {
                        setShowAllLogsModal(true)
                      },
                    }}
                    titleProps={{
                      title: 'View all inventory logs',
                    }}
                  />
                </div>
                <div className='m-3'>
                  {logs.map((log, index) => {
                    return (
                      <InventoryCorrectionsRow
                        modify={(logItem: IInventoryLog) =>
                          setSelectedInventoryLogToModify(logItem)
                        }
                        canEdit={canUpdateInventoryLogs}
                        log={log}
                        key={index}
                      />
                    )
                  })}
                </div>
              </Collapse>
            </td>
          </tr>

          {!!selectedInventoryLogToModify && (
            <ModifyLogModalOCS
              log={selectedInventoryLogToModify}
              onHide={() => setSelectedInventoryLogToModify(undefined)}
              onUpdate={onUpdate}
            />
          )}

          {showAllLogsModal && (
            <ViewAllLogsModalOCS
              companyId={companyId}
              onHide={() => setShowAllLogsModal(false)}
              productId={productId}
              productName={productName}
              locationIds={locationIds}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </>
      )}
    </>
  )
}

export const InventoryCorrectionsLogs = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(_InventoryCorrectionsLogs))
